import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft } from "lucide-react";

const SpanishLevelTest = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [testStarted, setTestStarted] = useState(false);

  const questions = [
    // A1 Grammar Questions
    {
      level: "A1",
      question: "Yo ____ estudiante.",
      options: [
        { id: "a", text: "es", correct: false },
        { id: "b", text: "soy", correct: true },
        { id: "c", text: "eres", correct: false },
        { id: "d", text: "son", correct: false }
      ],
      explanation: "Verb 'ser' (to be) - 1st person singular: yo soy"
    },
    {
      level: "A1",
      question: "Ella ____ dos hermanos.",
      options: [
        { id: "a", text: "tienen", correct: false },
        { id: "b", text: "tiene", correct: true },
        { id: "c", text: "tengo", correct: false },
        { id: "d", text: "tienes", correct: false }
      ],
      explanation: "Verb 'tener' (to have) - 3rd person singular: ella tiene"
    },
    {
      level: "A1",
      question: "Nosotros ____ al cine los sábados.",
      options: [
        { id: "a", text: "va", correct: false },
        { id: "b", text: "vas", correct: false },
        { id: "c", text: "vamos", correct: true },
        { id: "d", text: "van", correct: false }
      ],
      explanation: "Verb 'ir' (to go) - 1st person plural: nosotros vamos"
    },
    {
      level: "A1",
      passage: "¡Hola! Me llamo Carlos. Vivo en Madrid. Tengo 28 años y trabajo como ingeniero en una empresa de tecnología.",
      question: "¿Dónde vive Carlos?",
      options: [
        { id: "a", text: "En Barcelona", correct: false },
        { id: "b", text: "En Madrid", correct: true },
        { id: "c", text: "En Valencia", correct: false },
        { id: "d", text: "En Sevilla", correct: false }
      ],
      explanation: "Carlos says 'Vivo en Madrid' which means 'I live in Madrid'"
    },
    {
      level: "A1",
      passage: "Ana le encanta leer libros. Ella lee todas las noches antes de dormir. Sus libros favoritos son las novelas románticas.",
      question: "¿Cuándo lee Ana?",
      options: [
        { id: "a", text: "Por la mañana", correct: false },
        { id: "b", text: "Por la noche", correct: true },
        { id: "c", text: "Al mediodía", correct: false },
        { id: "d", text: "Por la tarde", correct: false }
      ],
      explanation: "'Ella lee todas las noches' means 'she reads every night'"
    },
    // A2 Questions
    {
      level: "A2",
      question: "Ayer, yo ____ al parque con mis amigos.",
      options: [
        { id: "a", text: "voy", correct: false },
        { id: "b", text: "fui", correct: true },
        { id: "c", text: "iré", correct: false },
        { id: "d", text: "iba", correct: false }
      ],
      explanation: "Pretérito indefinido: fui is the past tense of ir"
    },
    {
      level: "A2",
      question: "Ella ____ la tarea cuando sonó el teléfono.",
      options: [
        { id: "a", text: "hace", correct: false },
        { id: "b", text: "hizo", correct: false },
        { id: "c", text: "hacía", correct: true },
        { id: "d", text: "hará", correct: false }
      ],
      explanation: "Imperfecto for ongoing action in the past: hacía"
    },
    {
      level: "A2",
      question: "¿Me pasas ____ sal, por favor?",
      options: [
        { id: "a", text: "el", correct: false },
        { id: "b", text: "la", correct: true },
        { id: "c", text: "un", correct: false },
        { id: "d", text: "una", correct: false }
      ],
      explanation: "Definite article with feminine noun: la sal"
    },
    {
      level: "A2",
      passage: "La semana pasada, Laura fue al mercado. Compró frutas, verduras y queso. Luego regresó a casa para preparar la comida.",
      question: "¿Qué hizo Laura después del mercado?",
      options: [
        { id: "a", text: "Fue al cine", correct: false },
        { id: "b", text: "Regresó a casa", correct: true },
        { id: "c", text: "Fue a trabajar", correct: false },
        { id: "d", text: "Llamó a una amiga", correct: false }
      ],
      explanation: "'Luego regresó a casa' means 'Then she returned home'"
    },
    {
      level: "A2",
      passage: "Miguel adora viajar. Ya ha visitado Italia, Francia y Portugal. El próximo año, quiere ir a Japón.",
      question: "¿Dónde quiere ir Miguel el próximo año?",
      options: [
        { id: "a", text: "A Italia", correct: false },
        { id: "b", text: "A Francia", correct: false },
        { id: "c", text: "A Japón", correct: true },
        { id: "d", text: "A Portugal", correct: false }
      ],
      explanation: "'El próximo año, quiere ir a Japón' means 'Next year, he wants to go to Japan'"
    },
    // B1 Questions
    {
      level: "B1",
      question: "Si tuviera dinero, ____ de viaje.",
      options: [
        { id: "a", text: "voy", correct: false },
        { id: "b", text: "iré", correct: false },
        { id: "c", text: "iría", correct: true },
        { id: "d", text: "fui", correct: false }
      ],
      explanation: "Conditional: si + imperfect subjunctive, conditional simple"
    },
    {
      level: "B1",
      question: "Es importante que tú ____ más atención en clase.",
      options: [
        { id: "a", text: "pones", correct: false },
        { id: "b", text: "pongas", correct: true },
        { id: "c", text: "poner", correct: false },
        { id: "d", text: "pondrías", correct: false }
      ],
      explanation: "Subjunctive after 'es importante que': que tú pongas"
    },
    {
      level: "B1",
      question: "Este edificio fue ____ por un arquitecto famoso.",
      options: [
        { id: "a", text: "construir", correct: false },
        { id: "b", text: "construyendo", correct: false },
        { id: "c", text: "construido", correct: true },
        { id: "d", text: "construye", correct: false }
      ],
      explanation: "Passive voice: fue + past participle (construido)"
    },
    {
      level: "B1",
      passage: "El teletrabajo tiene ventajas y desventajas. Por un lado, permite ahorrar tiempo de desplazamiento y ser más flexible. Por otro lado, algunos empleados se sienten aislados y tienen dificultades para separar la vida laboral de la personal.",
      question: "Según el texto, ¿cuál es una ventaja del teletrabajo?",
      options: [
        { id: "a", text: "Se ahorra tiempo de desplazamiento", correct: true },
        { id: "b", text: "Uno se siente menos aislado", correct: false },
        { id: "c", text: "El trabajo es más fácil", correct: false },
        { id: "d", text: "Se trabaja menos", correct: false }
      ],
      explanation: "'Permite ahorrar tiempo de desplazamiento' is clearly mentioned as an advantage"
    },
    {
      level: "B1",
      passage: "Para aprender bien un idioma extranjero, no basta con memorizar vocabulario. Es esencial practicar regularmente, sumergirse en la cultura y atreverse a hablar aunque se cometan errores.",
      question: "Según el texto, ¿qué hay que hacer para aprender un idioma?",
      options: [
        { id: "a", text: "Solo memorizar", correct: false },
        { id: "b", text: "Practicar e inmersión en la cultura", correct: true },
        { id: "c", text: "No cometer errores nunca", correct: false },
        { id: "d", text: "Evitar hablar", correct: false }
      ],
      explanation: "The text emphasizes practice, immersion, and speaking despite mistakes"
    },
    // B2 Questions
    {
      level: "B2",
      question: "Aunque ____ lloviendo, decidimos salir.",
      options: [
        { id: "a", text: "está", correct: false },
        { id: "b", text: "estaba", correct: true },
        { id: "c", text: "estará", correct: false },
        { id: "d", text: "estaría", correct: false }
      ],
      explanation: "Imperfect indicative after 'aunque' when referring to past"
    },
    {
      level: "B2",
      question: "No solo es inteligente, ____ también muy amable.",
      options: [
        { id: "a", text: "pero", correct: false },
        { id: "b", text: "sino", correct: true },
        { id: "c", text: "y", correct: false },
        { id: "d", text: "o", correct: false }
      ],
      explanation: "'No solo... sino también' is the correct correlative conjunction"
    },
    {
      level: "B2",
      question: "Con tal de que ____ buen tiempo, iremos a la playa.",
      options: [
        { id: "a", text: "hace", correct: false },
        { id: "b", text: "haga", correct: true },
        { id: "c", text: "hará", correct: false },
        { id: "d", text: "hacía", correct: false }
      ],
      explanation: "Subjunctive after 'con tal de que': que haga"
    },
    {
      level: "B2",
      passage: "El cambio climático representa uno de los mayores desafíos de nuestro tiempo. Aunque los científicos multiplican las advertencias, las medidas tomadas siguen siendo insuficientes. La transición energética requiere no solo inversiones masivas, sino también un cambio profundo en las mentalidades y comportamientos individuales y colectivos.",
      question: "Según el texto, ¿qué se necesita para la transición energética?",
      options: [
        { id: "a", text: "Solo dinero", correct: false },
        { id: "b", text: "Inversiones y cambio de mentalidades", correct: true },
        { id: "c", text: "Ignorar a los científicos", correct: false },
        { id: "d", text: "Esperar que pase", correct: false }
      ],
      explanation: "The text mentions both 'inversiones masivas' and 'cambio profundo en las mentalidades'"
    },
    {
      level: "B2",
      passage: "La inteligencia artificial genera tantas esperanzas como preocupaciones. Si bien promete avances considerables en campos como la medicina y la educación, también plantea preguntas éticas fundamentales sobre la privacidad, el empleo y la autonomía humana. Es necesario un debate democrático para regular su desarrollo.",
      question: "¿Por qué la inteligencia artificial necesita un debate democrático?",
      options: [
        { id: "a", text: "Es demasiado cara", correct: false },
        { id: "b", text: "Plantea preguntas éticas importantes", correct: true },
        { id: "c", text: "No funciona bien", correct: false },
        { id: "d", text: "Nadie la usa", correct: false }
      ],
      explanation: "The text mentions 'preguntas éticas fundamentales' as the reason for debate"
    }
  ];

  const handleAnswer = (questionIndex, optionId) => {
    setAnswers({ ...answers, [questionIndex]: optionId });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateResults = () => {
    let a1Score = 0;
    let a2Score = 0;
    let b1Score = 0;
    let b2Score = 0;

    questions.forEach((question, index) => {
      const selectedOption = answers[index];
      const correctOption = question.options.find(opt => opt.correct);
      
      if (selectedOption === correctOption.id) {
        switch (question.level) {
          case "A1":
            a1Score++;
            break;
          case "A2":
            a2Score++;
            break;
          case "B1":
            b1Score++;
            break;
          case "B2":
            b2Score++;
            break;
          default:
            break;
        }
      }
    });

    return { a1Score, a2Score, b1Score, b2Score };
  };

  const determineLevel = () => {
    const scores = calculateResults();
    const total = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;
    const percentage = (total / questions.length) * 100;

    if (scores.b2Score >= 3 && scores.b1Score >= 3 && percentage >= 75) {
      return { level: "B2", description: "Upper Intermediate - You have a good command of complex grammar structures" };
    } else if (scores.b1Score >= 3 && scores.a2Score >= 3 && percentage >= 60) {
      return { level: "B1", description: "Intermediate - You can handle most grammar structures with confidence" };
    } else if (scores.a2Score >= 3 && scores.a1Score >= 3 && percentage >= 45) {
      return { level: "A2", description: "Elementary - You can use basic grammar structures with confidence" };
    } else {
      return { level: "A1", description: "Beginner - You are starting to build the foundations of the language" };
    }
  };

  const currentQ = questions[currentQuestion];

  if (!testStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Button
              variant="ghost"
              className="text-white hover:text-white/80 mb-4"
              onClick={() => navigate("/")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="test-title">
              Spanish Level Test
            </h1>
            <p className="text-xl text-white/90">Assess your level from A1 to B2</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl">
            <CardContent className="p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-[#04539F] mb-6">How much Spanish do you know?</h2>
              <div className="space-y-4 text-gray-700 mb-8">
                <p className="text-lg">Complete our free level test and discover your level.</p>
                <div className="flex gap-2 justify-center my-6">
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">A2</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B1</span>
                  <span className="px-3 py-1 bg-[#04539F] text-white rounded-full text-sm">B2</span>
                </div>
                <div className="bg-[#04539F]/10 rounded-lg p-6 space-y-2">
                  <ul className="list-disc list-inside space-y-2">
                    <li>The test will take 10 to 15 minutes</li>
                    <li>Select the correct answer for each question</li>
                    <li>Take your time - there's no time limit</li>
                    <li>Discover your level after completing the test</li>
                  </ul>
                </div>
              </div>
              <Button
                size="lg"
                className="w-full bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full py-6 text-lg"
                onClick={() => setTestStarted(true)}
                data-testid="start-test-btn"
              >
                Start Level Test
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (showResults) {
    const result = determineLevel();
    const scores = calculateResults();
    const totalCorrect = scores.a1Score + scores.a2Score + scores.b1Score + scores.b2Score;

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
        <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h1 className="text-4xl font-bold text-white">Level Test Completed!</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="shadow-2xl mb-8">
            <CardContent className="p-8 sm:p-12">
              <div className="text-center mb-8">
                <div className="inline-block bg-[#04539F] text-white rounded-full px-12 py-6 mb-4">
                  <span className="text-6xl font-bold">{result.level}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">{result.description}</h2>
                <p className="text-xl text-gray-600">You answered {totalCorrect} out of {questions.length} questions correctly</p>
              </div>

              <div className="bg-[#04539F]/10 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-bold text-[#04539F] mb-4">Your Answers</h3>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {questions.map((q, idx) => {
                    const userAnswer = answers[idx];
                    const correctAnswer = q.options.find(opt => opt.correct);
                    const isCorrect = userAnswer === correctAnswer.id;
                    
                    return (
                      <div key={idx} className="bg-white rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                            {isCorrect ? '✓' : '✗'}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800 mb-2">{q.question}</p>
                            {!isCorrect && (
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold">Correct answer:</span> {correctAnswer.text}
                                <br />
                                <span className="text-gray-500 italic">{q.explanation}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentQuestion(0);
                    setAnswers({});
                    setShowResults(false);
                    setTestStarted(false);
                  }}
                  className="rounded-full"
                >
                  Retake Test
                </Button>
                <Button
                  className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full"
                  onClick={() => navigate("/contact")}
                >
                  Contact Berni
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-white">Spanish Level Test</h1>
            <div className="text-white text-sm">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>
          <div className="mt-4 bg-white/20 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <div className="mb-6">
              {currentQ.passage && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <p className="text-gray-800 leading-relaxed italic">{currentQ.passage}</p>
                </div>
              )}
              <h3 className="text-2xl font-bold text-[#04539F] mb-6">{currentQ.question}</h3>
            </div>

            <div className="space-y-3 mb-8">
              {currentQ.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleAnswer(currentQuestion, option.id)}
                  className={`w-full text-left p-5 rounded-lg border-2 transition-all text-lg ${
                    answers[currentQuestion] === option.id
                      ? 'border-[#04539F] bg-[#04539F]/10'
                      : 'border-gray-200 hover:border-[#04539F]/50'
                  }`}
                  data-testid={`option-${option.id}`}
                >
                  <div className="flex items-center">
                    <div className={`w-7 h-7 rounded-full border-2 mr-4 flex items-center justify-center ${
                      answers[currentQuestion] === option.id
                        ? 'border-[#04539F] bg-[#04539F] text-white'
                        : 'border-gray-300'
                    }`}>
                      {answers[currentQuestion] === option.id && '✓'}
                    </div>
                    <span className="font-medium text-gray-800">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion]}
                className="bg-[#04539F] hover:bg-[#033b7a] text-white rounded-full px-8"
                data-testid="next-btn"
              >
                {currentQuestion === questions.length - 1 ? 'Finish Test' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SpanishLevelTest;
